import "cleave.js/dist/cleave-angular.js";
import DateUtil from "../../util/DateUtil";
import MaskUtil from "../../util/MaskUtil";

import beneficiaryDateViewTemplate from "./BeneficiaryDateViewTemplate";

const BeneficiaryDateDirective = angular
    .module("BeneficiaryDateDirective", ["cleave.js"])
    .directive("pwBeneficiaryDate", dateDirective)
    .directive("pwBeneficiaryDateValidator", dateValidator);

/**
 *  NOTE: The original DateDirective is causing issues in mobile screens where the masks aren't applying/functioning
 *          correctly. This is due to the use of jquery inputmask in the pwMask directive which is a known issue.
 *          There are numerous directives for items like DOB SSN being used within the application. There needs to be
 *          an effort to consolidate into a single directive that works across all apps.
 */
function dateDirective() {
    return {
        restrict: "E",
        template: beneficiaryDateViewTemplate,
        priority: 1000,
        scope: {
            name: "@dateName",
            id: "@dateId",
            title: "@dateTitle",
            class: "@dateClass",
            model: "=dateModel",
            isRequired: "&",
            isDisabled: "&",
            minDate: "=",
            maskDate: "=",
            allowEmpty: "=",
            allowFutureDate: "=",
            allowCurrentDate: "=",
            uiOptions: "=",
            dateBlur: "&",
            dateFocus: "&"
        },
        bindToController: true,
        controllerAs: "ctrl",
        controller: function () {
            if (typeof this.class == "undefined") {
                this.class = "form-control";
            }
            if (typeof this.uiOptions == "undefined") {
                this.uiOptions = "";
            }
        }
    };
}

function dateValidator() {
    /**
     *  return true if the specified date is not after current date..
     *  logic is not performed unless allowFutureDate === 'false'.
     */
    function doValidateFutureDate(modelValue, viewValue, allowFutureDate) {
        //console.log('directive... ($futureDate):  modelValue=['+modelValue+'], viewValue=['+viewValue+']');

        //bypass this for 'already masked values'
        if (modelValue && modelValue.indexOf(MaskUtil.MASK_CHAR) !== -1) {
            return true;
        }

        let valid = true;

        if (allowFutureDate === "false") {
            // Pass through until we get a complete date
            const mmddYYYY = viewValue;
            if (mmddYYYY !== null) {
                // The date is valid if it is not after today
                valid = !DateUtil.isAfter(
                    DateUtil.getGenericFormat(mmddYYYY, "MMDDYYYY"),
                    DateUtil.getDate()
                );
            }
        }

        //console.log('directive...futureDate valid? valid=['+valid+'], modelValue=['+modelValue+'], viewValue=['+viewValue+']');

        return valid;
    }

    /**
     *  return true if the specified date is not current date..
     *  logic is not performed unless allowCurrentDate === 'false'.
     */
    function doValidateCurrentDate(modelValue, viewValue, allowCurrentDate) {
        //bypass this for 'already masked values'
        if (modelValue && modelValue.indexOf(MaskUtil.MASK_CHAR) !== -1) {
            //console.log('directive... ($minYear):  already masked..skipping. modelValue=['+modelValue+'], viewValue=['+viewValue+']');
            return true;
        }
        let valid = true;
        if (viewValue && allowCurrentDate === "false") {
            const formattedInputDate = DateUtil.getDateFormatted("MM/DD/YYYY", viewValue);
            if (
                formattedInputDate &&
                DateUtil.isSame(formattedInputDate, DateUtil.getDateFormatted("MM/DD/YYYY"))
            ) {
                valid = false;
            }
        }
        return valid;
    }

    /**
     *  return true if the specified date validates against minDate.
     *  minDate logic skipped if minDate is null or undefined.
     */
    function doValidateMinDate(modelValue, viewValue, minDate) {
        //console.log('directive... ($minYear):  modelValue=['+modelValue+'], viewValue=['+viewValue+'], minDate=['+minDate+']');

        //bypass this for 'already masked values'
        if (modelValue && modelValue.indexOf(MaskUtil.MASK_CHAR) !== -1) {
            //console.log('directive... ($minYear):  already masked..skipping. modelValue=['+modelValue+'], viewValue=['+viewValue+']');
            return true;
        }

        let valid = true;

        if (minDate) {
            // Pass through until we get a complete date
            const mmddYYYY = modelValue;
            if (mmddYYYY !== null) {
                // The date is valid if it is on or after the minimum date
                valid = DateUtil.isBefore(
                    minDate,
                    DateUtil.getDateFormatted("YYYY-MM-DD", viewValue)
                );
            }
        }

        //console.log('directive...minYear valid? valid=['+valid+'], modelValue=['+modelValue+'], viewValue=['+viewValue+']');

        return valid;
    }

    /**
     *  perform rudimentary value validations.
     */
    function doValidate(modelValue, viewValue, allowEmpty) {
        //console.log('directive...modelValue=['+modelValue+'], viewValue=['+viewValue+'] allowEmpty=['+allowEmpty+']');
        let valid = true;

        if (viewValue === undefined || (viewValue === "" && allowEmpty)) {
            //empty is allowed
            return true;
        }
        if (viewValue === undefined || viewValue === "") {
            //console.log('directive...viewValue? valid=['+valid+'], modelValue=['+modelValue+'], viewValue=['+viewValue+']');
            return false;
        }

        if (modelValue === undefined || modelValue === null || modelValue === "") {
            valid = false;
            //console.log('directive...modelValue? valid=['+valid+'], modelValue=['+modelValue+'], viewValue=['+viewValue+']');
            return valid;
        }

        //masked is valid.
        if (modelValue && modelValue.indexOf(MaskUtil.MASK_CHAR) !== -1) {
            //console.log('directive...modelValue is already masked ['+modelValue+']');
            return true;
        }

        const dateValue = modelValue.replace("/", "").replace("/", "");

        if (dateValue.length !== 8) {
            valid = false;
            //console.log('directive...modelValue.length? valid=['+valid+'], dateValue=['+dateValue+'], modelValue=['+modelValue+'], viewValue=['+viewValue+']');
            return valid;
        }

        // Check the validity
        if (dateValue.length > 0) {
            const date = DateUtil.getDateFormatted("MMDDYYYY", viewValue);
            if (DateUtil.isValid(date, "MMDDYYYY", true)) {
                valid = true;
            }
        } else {
            valid = false;
            //console.log('directive...badDate? valid=['+valid+'], dateValue=['+dateValue+'], modelValue=['+modelValue+'], viewValue=['+viewValue+']');
        }

        const inputDate = viewValue.split("/");
        if (inputDate.length !== 3) {
            valid = false;
        }

        const month = parseInt(inputDate[0], 10);
        const day = parseInt(inputDate[1], 10);
        const year = parseInt(inputDate[2], 10);

        if (month < 1 || month > 12) {
            valid = false;
        }

        const daysInMonth = new Date(year, month, 0).getDate();
        if (day < 1 || day > daysInMonth) {
            valid = false;
        }

        return valid;
    }

    return {
        restrict: "A",
        require: "ngModel",
        link: function (scope, elem, attr, ctrl) {
            const maskDate = attr.maskDate === undefined ? false : attr.maskDate;
            const minDate = attr.minDate;
            const allowFutureDate = attr.allowFutureDate;
            const allowCurrentDate = attr.allowCurrentDate;
            const allowEmpty = attr.allowEmpty === undefined ? false : attr.allowEmpty;

            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            let newValue = null;

            elem.bind("blur", function () {
                scope.$apply(function () {
                    if (!MaskUtil.isMasked(elem.val())) {
                        //strip non-digits
                        // eslint-disable-next-line no-useless-escape
                        elem.val(elem.val().replace(/[^0-9\/]/g, ""));
                    }
                    newValue = elem.val();
                    //console.log('blur: newValue=['+newValue+'], elem=['+elem.val()+']');
                });
            });
            elem.bind("keyup", function (event) {
                elem.val(DateUtil.formatDateInputField(elem.val(), event.keyCode));
                scope.$apply(function () {
                    if (!MaskUtil.isMasked(elem.val())) {
                        //strip non-digits
                        // eslint-disable-next-line no-useless-escape
                        elem.val(elem.val().replace(/[^0-9\/]/g, ""));
                    }
                    newValue = elem.val();
                    //console.log('keyup: newValue=['+newValue+'], elem=['+elem.val()+']');
                });
            });
            elem.bind("keydown", function () {
                scope.$apply(function () {
                    if (!MaskUtil.isMasked(elem.val())) {
                        //strip non-digits
                        // eslint-disable-next-line no-useless-escape
                        elem.val(elem.val().replace(/[^0-9\/]/g, ""));
                    }
                    newValue = elem.val();
                    //console.log('keydown: newValue=['+newValue+'], elem=['+elem.val()+']');
                });
            });

            ctrl.$validators.valid = function (modelValue, viewValue) {
                //console.log('directive... ($validate):  modelValue=['+modelValue+'], viewValue=['+viewValue+']');
                const valid = doValidate(modelValue, viewValue, allowEmpty);
                if (valid && maskDate) {
                    /**
                     * if the other validations will pass then proceed w/ masking..
                     */
                    const minDateValid = doValidateMinDate(modelValue, viewValue, minDate);

                    if (minDateValid && minDateValid) {
                        //console.log('directive... ($validate):  valid && maskDate...masking');
                        elem.val(MaskUtil.maskDOB(viewValue));
                    }
                }
                //console.log('directive... ($validate): valid=['+valid+'], maskDate=['+maskDate+'], elem=['+elem.val()+'], modelValue=['+modelValue+'], viewValue=['+viewValue+']');
                return valid;
            };

            // Only apply the minYear validator if a minimum was defined
            ctrl.$validators.minYear = function (modelValue, viewValue) {
                return doValidateMinDate(modelValue, viewValue, minDate);
            };

            ctrl.$validators.futureDateNotAllowed = function (modelValue, viewValue) {
                return doValidateFutureDate(modelValue, viewValue, allowFutureDate);
            };

            ctrl.$validators.currentDateNotAllowed = function (modelValue, viewValue) {
                return doValidateCurrentDate(modelValue, viewValue, allowCurrentDate);
            };
        }
    };
}

export default BeneficiaryDateDirective;
